import type { LineStroke } from "./chartDots";
import {
  cadenceMs,
  daysSince,
  freshness,
  isGap,
  trainingLayoffBreaks,
  type Point,
} from "@heraion/shared";

// Pure annotation logic for the interactive line charts — freshness and the
// silences worth naming. Renderer-agnostic, provable without mounting a chart.
//
// ⛔ THIS FILE USED TO CUT THE LINE, and stopped on 2026-09-18 (PG-36,
// Antenore's call). The history matters, because the rule it dropped was a
// deliberate one and deleting it silently would invite someone to re-add it.
//
// HM-2 broke a series wherever a silence outran that series' own habit, so a
// hole stayed EMPTY instead of being crossed by a straight line asserting
// values nobody ever measured. That rule was written when `Point.x` was the
// point's INDEX, not its instant: on an index axis two readings three weeks
// apart sat exactly as close as two from the same morning, so breaking the
// path was the only way a silence could be seen at all.
//
// `PROG-X` shipped the temporal axis. A silence is now visible for free — it
// is a long stretch of axis with no dot on it — and the trailing one, the
// case that actually burned us (a dead feed nobody noticed for five days),
// is visible because the axis ends at `now` rather than at the last sample
// (`WagmiLineChart.tsx`, "ONE time axis"). So the cut had become the renderer
// paying for a defect that was already fixed — and paying a lot: wagmi draws
// ONE continuous path per instance, so N pieces of curve cost N chart
// instances. Measured on 83 rows of real data: 51 instances, four of which
// existed to draw a single dot.
//
// ⚠️ It also cut in the WRONG PLACES. The threshold reads `cadenceMs`, the
// median of the last TEN intervals, and applies that one number to the whole
// history. An athlete who weighs himself daily now and weighed himself every
// few days in June had June judged by September's rhythm: eleven cuts, of
// which eight said "you used to weigh yourself less often", not "the series
// stopped".
//
// What survives, because none of it ever depended on cutting:
//  - `stale` / `current` — "now" is still EARNED by being recent, never by
//    being last (HM-1). This is what still answers "is the source dead?".
//  - `gapDays` — a silence worth naming is still named, in words, on the dot.
//    It is now the ONLY place a gap is stated, so it carries more than it
//    used to, not less; it was already the only form a screen reader got.

// How a series is drawn, and the only thing that decides its colour and
// stroke. Named once: the union was written out by hand in five places
// across two files, and PG-26 added a sixth consumer outside the chart.
export type ChartVariant = "primary" | "secondary" | "focal";

export type ChartSeries = {
  key: string;
  points: Point[];
  variant?: ChartVariant;
  /** Which family this series belongs to. A `sampled` quantity (weight,
   *  glucose) exists between two readings, so its own cadence says when a
   *  silence is worth naming. An `event` one (e1RM, density) is produced BY
   *  training, so a pause in TRAINING is the thing worth naming — an exercise
   *  you simply did not do that week is not a silence. Defaults to `sampled`.
   *  ⛔ Since PG-36 this chooses what gets SAID, never where a line breaks. */
  family?: "sampled" | "event";
  /** Every session the athlete logged, ascending. Read only for `event`. */
  trainingAtMs?: readonly number[];
  /** Declares this series as the one a tap reads (PG-64 fetta 3) and the
   *  a11y summary's "latest" line (PG-31/PG-33). Distinct from `current` on
   *  the annotation below: `current` says the DATA is fresh, `headline` says
   *  the chart's INTERACTION follows it; a stale series can still be
   *  headline. At most one series per chart should set this; if none does,
   *  the last series drawn is (`resolveHeadlineIndex`). */
  headline?: boolean;
  /** Readable name of the series, for the a11y label prefix when 2+ series
   *  share a chart (PG-53). Absent = no prefix, same as before. */
  name?: string;
  /** #389: a line style of its own, for curves of one colour. Absent: the
   *  variant's (`lineStroke`). */
  stroke?: LineStroke;
};

export type ChartDot = {
  point: Point;
  /** Days since the previous point, and only when that interval is long
   *  enough that the series' own habit — or, for an event series, the
   *  athlete's training calendar — would call it a real silence. */
  gapDays?: number;
};

/** One series, drawn as ONE continuous path, plus what is true about it. */
export type ChartSeriesAnnotation = {
  key: string;
  variant: ChartVariant;
  /** Every point, in order. One run: there is no second one. */
  points: Point[];
  dots: ChartDot[];
  last: number;
  stale: boolean;
  /** FRESHNESS: is this reading recent? Never decides which series a tap
   *  reads: see `headline` below, which is the OWNERSHIP question. */
  current: boolean;
  /** OWNERSHIP: does a tap read this series, and the a11y headline? Read
   *  straight from `ChartSeries.headline` (default false); the fallback when
   *  nothing declares it is `resolveHeadlineIndex`. */
  headline: boolean;
  /** See `ChartSeries.name`. Read straight through, never guessed here. */
  name?: string;
  /** See `ChartSeries.stroke`. Read straight through. */
  stroke?: LineStroke;
};

/** Annotates every series against `nowMs` — the ONE notion that decides
 *  whether a last point is "now" and which intervals are worth naming (both
 *  read the same calendar for an event series, so the drawing never
 *  contradicts itself — see `freshness.ts`). */
export function annotateChartSeries(
  series: ChartSeries[],
  nowMs: number,
): ChartSeriesAnnotation[] {
  return series.map((s) => annotateSeries(s, nowMs));
}

function annotateSeries(s: ChartSeries, nowMs: number): ChartSeriesAnnotation {
  const variant = s.variant ?? "primary";
  const xs = s.points.map((p) => p.x);
  const cadence = cadenceMs(xs);
  // An event series is judged against the training calendar, not against its
  // own spacing. Without a calendar it falls back to cadence: no calendar, no
  // claim about which family this is.
  const eventCalendar = s.family === "event" ? s.trainingAtMs : undefined;
  const eventBreaks =
    eventCalendar === undefined ? null : trainingLayoffBreaks(xs, eventCalendar);

  const dots: ChartDot[] = s.points.map((p, i) => {
    const prev = i > 0 ? s.points[i - 1] : undefined;
    if (prev === undefined) return { point: p };
    // Both reasons count (PG-23, 16/09): what the series' own cadence calls a
    // silence, AND what the training calendar calls a layoff. ⛔ Cadence alone
    // is not enough — on a SHORT series `cadenceMs` returns null (it wants 5
    // points), so the interval would go unnamed exactly where data is scarce.
    // ⛔ `daysSince` is THE implementation (CN-day-math): no sixth hand-rolled
    // copy. Under a whole day nothing is said — "+0d" is noise and "+1d" is an
    // invented number.
    const worthNaming =
      (eventBreaks !== null && eventBreaks[i] === true) ||
      isGap(p.x - prev.x, cadence);
    const gapDays = worthNaming ? (daysSince(prev.x, p.x) ?? 0) : 0;
    return { point: p, ...(gapDays >= 1 ? { gapDays } : {}) };
  });

  // "Now" is a claim about time, so it is EARNED by being recent — never by
  // being last, and never by default. Three states, not two (Antenore,
  // 2026-07-15 — «niente arancione senza prove»): unknown cadence (too few
  // points to have a rhythm) withholds the accent WITHOUT calling the point
  // stale either, because we cannot call a datum stale when we don't know
  // what its normal looks like.
  const fresh = freshness(eventCalendar ?? xs, nowMs);
  const stale = fresh.known && fresh.stale;
  const current = fresh.known && !fresh.stale;

  const last = s.points[s.points.length - 1]?.y ?? 0;
  return {
    key: s.key,
    variant,
    points: s.points,
    dots,
    last,
    stale,
    current,
    headline: s.headline ?? false,
    name: s.name,
    ...(s.stroke ? { stroke: s.stroke } : {}),
  };
}

/** Which series a tap reads and the a11y summary quotes: the first one whose
 *  `ChartSeries.headline` is true, else the LAST one drawn. -1 for an empty
 *  chart. */
export function resolveHeadlineIndex(
  built: readonly Pick<ChartSeriesAnnotation, "headline">[],
): number {
  const declared = built.findIndex((b) => b.headline);
  return declared >= 0 ? declared : built.length - 1;
}
